import APIService from 'api/APIService'
import { AxiosError } from 'axios'
import { LoginUrl } from 'constants/RouteUrls'
import Button from 'core/components/buttons/Button'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import CardPageWrapper from 'core/components/page-wrapper/CardPageWrapper'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router'
import { NextActionResponse } from 'types/user'
import { ErrorMessage } from 'types/utility'
import { getErrorMessage } from 'utils/AppHelper'

type FieldValues = {
  username: string
}

const PurplePasswordResetPage = () => {
  const { push } = useNavigation()
  const { control, handleSubmit } = useForm<FieldValues>()
  const {
    mutate,
    status,
    isError,
    error,
    isPending: isLoading
  } = useMutation({
    mutationKey: ['request', 'password', 'reset'],
    mutationFn: ({ username }: { username: string }) => {
      // Motus API support both user ids and emails
      return APIService.motus.requestPasswordReset({ email: username })
    },
    onError: (error: AxiosError<NextActionResponse>, { username }) => {
      if (error?.response?.data?.next_action === 'motus_password_reset') {
        mutate({ username })
      }
    }
  })

  const onSubmit = useCallback(
    ({ username }: FieldValues) => {
      mutate({ username })
    },
    [mutate]
  )

  const title = useMemo(() => {
    if (status === 'success') {
      return 'Check your inbox'
    }
    return 'Forgot password'
  }, [status])

  const subtitle = useMemo(() => {
    if (status === 'success') {
      return (
        <>
          <p className="mb-4">
            We have sent you an email to reset your password.
          </p>
          <p>Please, proceed in the email app.</p>
        </>
      )
    }
    return <p>Please enter in your username to reset your password.</p>
  }, [status])

  const actionButton = useMemo(() => {
    if (status === 'success') {
      return (
        <Button variant="primary" onClick={() => push(LoginUrl)}>
          Sign In
        </Button>
      )
    }
    return (
      <Button
        type="submit"
        variant="primary"
        className="disabled:text-white"
        status={status}
        disabled={isLoading}
      >
        Email Me a Reset Link
      </Button>
    )
  }, [push, status, isLoading])

  return (
    <CardPageWrapper
      backUrl={LoginUrl}
      company="motus"
      dataTitle="password-reset-screen"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-primary text-2xl font-bold w-full text-left">
            {title}
          </h2>
          <p className="text-md">{subtitle}</p>
        </div>
        {status !== 'success' && (
          <div className="flex flex-col">
            <SmartField
              label="Email/Username"
              orientation="vertical"
              controllerProps={{
                control,
                name: 'username',
                rules: {
                  required: 'Email/Username is required',
                  validate: {
                    noBlank: (value) =>
                      value.trim().length > 0 ||
                      'Email/Username cannot be just whitespace'
                  }
                }
              }}
            >
              <Input
                placeholder="username"
                type="text"
                className="text-lg mb-2 w-full py-3.5"
              />
            </SmartField>
            <Link
              className="text-primary underline hover:underline"
              to={LoginUrl}
            >
              Never mind! I'd like to Sign In
            </Link>
          </div>
        )}
        {actionButton}
        {isError && (
          <p className="pt-2 text-danger">
            {getErrorMessage(
              error as ErrorMessage,
              'Something went wrong, please try again later. If the error persists, contact us.'
            )}
          </p>
        )}
      </form>
    </CardPageWrapper>
  )
}

export default PurplePasswordResetPage
